// Fleet's reads, grouped analytics queries but for the tenant's provider
// policy, so they hold at any number of agents, models and endpoints: the
// server groups, orders and pages, and the browser joins a few thousand rows.
//
//   groups       CONVERSATION_TRACE (a session) joined to its capture frames
//                (CONVERSATION_SOURCE_OBSERVATION) for the agent type and the
//                capture source they report, grouped by surface, provider,
//                agent type and source: the split's first levels
//                (lib/fleetBreakdown.js), a session in each group its frames
//                fall in. With the model as well (links), the levels below
//                it. A span's traces, and each agent's, are counted as the
//                Overview counts them (lib/overviewReads.js), never from
//                these groups.
//   installed    AGENT_INVENTORY by product, on real (not synthetic) endpoints.
//   runtimes     the launches and GPU inference AI_SERVICE_OBSERVATION records
//                by endpoint, and local sessions (provider "local") by
//                endpoint.
//   agents       for Findings, the agents a set of sessions ran under, named
//                as the split names them (lib/fleetConsole.js resolveAgent).
//   endpoints    each endpoint's sessions, for a page of the endpoints table,
//                and the agents that made them; the sessions with any endpoint.
//   local        local sessions by model and endpoint, the runtimes installed
//                on each endpoint, and the endpoints' names and owners.
//   marks        a provider's mark, set by SetAiServicePolicy (Settings).
//
// Conversation entities need an explicit limit (at most 10,000 groups) and a
// window of at most 90 days; every read below names one. A part that cannot
// be read comes back null and the page says so; nothing reads as a zero.
import { rangeToTimeRange, rowsToObjects, runQuery } from "./analyticsClient.js";
import { restGet, restPost } from "./restClient.js";
import { toApiRange } from "./shell.js";
import { installedBy, runtimesIn, toPaths } from "./fleetBreakdown.js";
import { FLEET_RANGE, RUNTIME_PRODUCT_NAMES, dispositionOf, identifyAgents, mergeAgents, mergeProviders, sourceValues } from "./fleetConsole.js";

const ZERO = "00000000-0000-0000-0000-000000000000";
const GROUP_CAP = 10_000;
const IN_CAP = 1000;

const F = (alias, entity) => (name) => ({ stepAlias: alias, field: `DIMENSION_FIELD_${entity}_${name}` });
const T = F("t", "CONVERSATION_TRACE");
const O = F("o", "CONVERSATION_SOURCE_OBSERVATION");
const INV = F("i", "AGENT_INVENTORY");
const EP = F("e", "ENDPOINT");
const OBS = F("s", "AI_SERVICE_OBSERVATION");
const MC = F("mc", "ENDPOINT_METADATA_CHANGE");

const pred = (field, op, value) => ({ pred: { field, op: `OPERATOR_${op}`, ...(value === undefined ? {} : { value }) } });
const and = (...c) => { const kids = c.filter(Boolean); return kids.length === 0 ? null : kids.length === 1 ? kids[0] : { andExpr: { children: kids } }; };
const any = (...c) => { const kids = c.filter(Boolean); return kids.length === 0 ? null : kids.length === 1 ? kids[0] : { orExpr: { children: kids } }; };
const count = (field, outputAlias, predicate = null) => ({ outputAlias, computed: { field, op: "AGGREGATE_OP_COUNT_DISTINCT", ...(predicate ? { predicate } : {}) } });
const max = (field, outputAlias) => ({ outputAlias, computed: { field, op: "AGGREGATE_OP_MAX" } });
const dim = (field, outputAlias, extra = {}) => ({ field, outputAlias, ...extra });
// A range id ("1M"), or explicit { from, to } bounds such as a week of the window.
const timeRange = (range) => rangeToTimeRange(typeof range === "object" ? range : toApiRange(range));
const withFilter = (spec, filter) => (filter ? { ...spec, filter } : spec);
const desc = (outputAlias) => [{ outputAlias, direction: "DIRECTION_DESC" }];
const known = (id) => id && id !== ZERO;

// A trace, with its frames joined where a read needs them: LEFT so a trace
// with no frame (no agent type) still counts.
const FRAMES = (mode = "JOIN_MODE_LEFT") => ({ edge: "EDGE_TYPE_CONVERSATION_TRACE_HAS_SOURCE_OBSERVATIONS", mode, target: { alias: "o", entity: "ENTITY_TYPE_CONVERSATION_SOURCE_OBSERVATION" } });
const traceRoot = (mode = "JOIN_MODE_LEFT") => ({ alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE", children: [FRAMES(mode)] });
const traceFilters = (filters = {}) => and(
  filters.provider ? pred(T("PROVIDER"), "EQ", filters.provider) : null,
  filters.source ? pred(O("SOURCE_CAPTURE"), "IN", sourceValues(filters.source)) : null,
);

/**
 * The traces one agent covers (lib/fleetConsole.js agentMatch): any product
 * surface it is named by, or, per provider, a capture-only surface with one of
 * the agent types (or none, where `untyped`) that named it.
 */
export function agentPredicate(match, { surface = T("CLIENT_SURFACE"), provider = T("PROVIDER"), agentType = O("AGENT_TYPE") } = {}) {
  if (!match) return null;
  const NONE = ["", "0", "Unspecified", "AI_AGENT_TYPE_UNSPECIFIED", "unspecified", "unknown", "Unknown"];
  return any(
    match.surfaces?.length ? pred(surface, "IN", match.surfaces) : null,
    ...(match.providers || []).map((p) => and(
      pred(provider, "EQ", p.provider),
      pred(surface, "IN", p.surfaces.length ? p.surfaces : [""]),
      agentType ? any(
        p.types.length ? pred(agentType, "IN", p.types) : null,
        p.untyped ? any(pred(agentType, "IS_NULL"), pred(agentType, "IN", NONE)) : null,
      ) : null,
    )),
  );
}

// ---- specs ----------------------------------------------------------------

// Traces only, with no metric on the trace's endpoint: a predicate on it
// reads the trace's source associations, dated by their own time, which
// counted older windows high (Origin chat on OpenRouter read 289 traces where
// there were 37).
export function agentGroupsSpec({ range, filters = {}, extra = null, limit = GROUP_CAP, source = false }) {
  const dimensions = [dim(T("CLIENT_SURFACE"), "surface"), dim(T("PROVIDER"), "provider"), dim(O("AGENT_TYPE"), "agentType"), ...(source ? [dim(O("SOURCE_CAPTURE"), "source")] : [])];
  return withFilter({
    root: traceRoot(),
    timeRange: timeRange(range),
    output: { dimensions, metrics: [count(T("TRACE_ID"), "sessions"), max(T("EVENT_TIME"), "last")] },
    orderBy: desc("sessions"), limit,
  }, and(traceFilters(filters), extra));
}

const INVENTORY = { alias: "i", entity: "ENTITY_TYPE_AGENT_INVENTORY", children: [{ edge: "EDGE_TYPE_AGENT_INVENTORY_ON_ENDPOINT", mode: "JOIN_MODE_INNER", target: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" } }] };
export function installedSpec({ filters = {}, names = null, byEndpoint = false, limit = IN_CAP }) {
  return withFilter({
    root: INVENTORY,
    // The inventory is the latest snapshot; the server ignores the window.
    timeRange: timeRange("1M"),
    output: byEndpoint
      ? { dimensions: [dim(INV("ENDPOINT_ID"), "endpoint")] }
      : { dimensions: [dim(INV("AGENT_NAME"), "name"), dim(INV("PROVIDER"), "provider")], metrics: [count(INV("ENDPOINT_ID"), "installed")] },
    ...(byEndpoint ? { limit } : { orderBy: desc("installed"), limit }),
  }, and(
    pred(EP("IS_SYNTHETIC"), "EQ", false),
    filters.provider ? pred(INV("PROVIDER"), "EQ", filters.provider) : null,
    names ? pred(INV("AGENT_NAME"), "IN", names) : null,
  ));
}

// Trace groups that carry a model: which models each agent called.
export function linksSpec({ range, filters = {}, source = false }) {
  return withFilter({
    root: traceRoot("JOIN_MODE_INNER"), timeRange: timeRange(range),
    output: { dimensions: [dim(T("CLIENT_SURFACE"), "surface"), dim(T("PROVIDER"), "provider"), dim(O("AGENT_TYPE"), "agentType"), dim(O("MODEL"), "model"), ...(source ? [dim(O("SOURCE_CAPTURE"), "source")] : [])], metrics: [count(T("TRACE_ID"), "sessions")] },
    orderBy: desc("sessions"), limit: GROUP_CAP,
  }, and(traceFilters(filters), pred(O("MODEL"), "NE", "")));
}

// Local inference: sessions stamped provider "local", by machine.
export function localSpec({ range, endpoints = null }) {
  return withFilter({
    root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" }, timeRange: timeRange(range),
    output: { dimensions: [dim(T("ENDPOINT_ID"), "endpoint")], metrics: [count(T("TRACE_ID"), "sessions"), max(T("EVENT_TIME"), "last")] },
    orderBy: desc("sessions"), limit: GROUP_CAP,
  }, and(pred(T("PROVIDER"), "EQ", "local"), endpoints ? pred(T("ENDPOINT_ID"), "IN", endpoints.slice(0, IN_CAP)) : null));
}

// A runtime seen running: a recognized local runtime's launch, or GPU
// inference. Loaded libraries (ONNX, PyTorch in any app) are not evidence of
// a runtime serving models.
const RUNNING = ["process_execution", "gpu_inference"];
export function evidenceSpec({ range }) {
  return {
    root: { alias: "s", entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION" },
    timeRange: timeRange(range),
    filter: pred(OBS("EVIDENCE_KIND"), "IN", RUNNING),
    output: { dimensions: [dim(OBS("PROVIDER_TAG"), "tag"), dim(OBS("PROCESS_IMAGE"), "image"), dim(OBS("HOST_PROCESS_NAME"), "host"), dim(OBS("ENDPOINT_ID"), "endpoint")], metrics: [max(OBS("OBSERVED_AT"), "last")] },
    orderBy: desc("last"), limit: GROUP_CAP,
  };
}

// Local sessions (provider "local") by model and endpoint: what each
// endpoint ran on its own, each session once per model.
export function localModelsSpec({ range }) {
  return {
    root: traceRoot("JOIN_MODE_INNER"), timeRange: timeRange(range),
    filter: and(pred(T("PROVIDER"), "EQ", "local"), pred(O("MODEL"), "NE", "")),
    output: { dimensions: [dim(O("MODEL"), "model"), dim(T("ENDPOINT_ID"), "endpoint")], metrics: [count(T("TRACE_ID"), "sessions")] },
    orderBy: desc("sessions"), limit: GROUP_CAP,
  };
}

// The local runtimes installed on each real endpoint.
export function runtimesOnSpec() {
  return {
    root: INVENTORY,
    // The inventory is the latest snapshot; the server ignores the window.
    timeRange: timeRange("1M"),
    filter: and(pred(EP("IS_SYNTHETIC"), "EQ", false), pred(INV("AGENT_NAME"), "IN", RUNTIME_PRODUCT_NAMES)),
    output: { dimensions: [dim(INV("ENDPOINT_ID"), "endpoint"), dim(INV("AGENT_NAME"), "name")] },
    limit: GROUP_CAP,
  };
}

// Endpoints by id, with their names and whose they are.
export function endpointNamesSpec({ ids }) {
  return {
    root: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" }, timeRange: timeRange("1M"),
    filter: pred(EP("ENDPOINT_ID"), "IN", ids.slice(0, IN_CAP)),
    output: { dimensions: [dim(EP("ENDPOINT_ID"), "id"), dim(EP("HOSTNAME"), "hostname"), dim(EP("REGISTERED_USER"), "owner")] },
    limit: IN_CAP,
  };
}

// Some endpoints' sessions by the agent that made them, as the split names
// agents (toPaths), or by endpoint alone.
export function endpointAgentsSpec({ range, ids, agents = true }) {
  return {
    root: traceRoot(), timeRange: timeRange(range),
    filter: pred(T("ENDPOINT_ID"), "IN", ids.slice(0, IN_CAP)),
    output: {
      dimensions: [dim(T("ENDPOINT_ID"), "endpoint"), ...(agents ? [dim(T("CLIENT_SURFACE"), "surface"), dim(T("PROVIDER"), "provider"), dim(O("AGENT_TYPE"), "agentType"), dim(O("SOURCE_CAPTURE"), "source")] : [])],
      metrics: [count(T("TRACE_ID"), "sessions")],
    },
    orderBy: desc("sessions"), limit: GROUP_CAP,
  };
}

// The endpoints sessions came from, each with its sessions.
export function activeEndpointsSpec({ range }) {
  return {
    root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" }, timeRange: timeRange(range),
    filter: pred(T("ENDPOINT_ID"), "NE", ZERO),
    output: { dimensions: [dim(T("ENDPOINT_ID"), "endpoint")], metrics: [count(T("TRACE_ID"), "sessions")] },
    orderBy: desc("sessions"), limit: GROUP_CAP,
  };
}

// The endpoints whose inventory changed in a window: each was heard from then.
export function changedEndpointsSpec({ range }) {
  return {
    root: { alias: "mc", entity: "ENTITY_TYPE_ENDPOINT_METADATA_CHANGE" }, timeRange: timeRange(range),
    output: { dimensions: [dim(MC("ENDPOINT_ID"), "endpoint")] },
    limit: GROUP_CAP,
  };
}

// The sessions that came from any endpoint.
export function endpointSessionsSpec({ range }) {
  return {
    root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" }, timeRange: timeRange(range),
    filter: pred(T("ENDPOINT_ID"), "NE", ZERO),
    output: { metrics: [count(T("TRACE_ID"), "sessions"), count(T("ENDPOINT_ID"), "endpoints")] },
    limit: 1,
  };
}

// ---- reads ----------------------------------------------------------------

const run = (execute) => async (spec) => rowsToObjects(await execute(spec));
const settle = (p) => p.then((v) => v, (err) => { if (err?.name === "AbortError") throw err; console.warn("[fleet] read failed:", err); return null; });

const POLICIES = "/v1/ai-services/policies";

/**
 * Set a provider's mark. A response lost on the way back may still have
 * saved, so a failure reads the policy back: the mark the server holds is
 * the answer. Rejects when the server does not hold `mark`.
 */
export async function saveFleetProviderMark({ tag, mark }, post = restPost, get = restGet) {
  try {
    await post(`/v1/ai-services/${encodeURIComponent(tag)}:set-policy`, { provider_tag: tag, disposition: dispositionOf(mark) });
  } catch (err) {
    const back = await get(POLICIES).catch(() => null);
    if (mergeProviders({ policies: back?.policies || [] }).find((p) => p.tag === tag)?.mark !== mark) throw err;
  }
}

/**
 * The agents a set of sessions ran under (a Findings case's), named as the
 * fleet names them, each with the provider Explore searches it by. Read once
 * per set and range for the page's life; a failed read is asked again.
 */
const sessionAgents = new Map();
export function readSessionAgents({ sessions = [], range = FLEET_RANGE } = {}, execute = runQuery) {
  const ids = [...new Set(sessions.filter(Boolean))].slice(0, IN_CAP);
  if (!ids.length) return Promise.resolve([]);
  const key = `${range}|${ids.join(",")}`;
  if (!sessionAgents.has(key)) {
    sessionAgents.set(key, run(execute)(agentGroupsSpec({ range, extra: pred(T("TRACE_ID"), "IN", ids), limit: 50 }))
      .then((groups) => mergeAgents({ agents: identifyAgents(groups) }).map((a) => ({ id: a.id, name: a.name, provider: a.provider })))
      .catch((err) => { sessionAgents.delete(key); throw err; }));
    if (sessionAgents.size > 200) sessionAgents.delete(sessionAgents.keys().next().value);
  }
  return sessionAgents.get(key);
}

/**
 * The Fleet breakdown over a span (`range`, lib/fleetConsoleStore.js
 * fleetWindows): its sessions as groups by agent and how they reached Origin,
 * and again by model, with the installed products; with `before`, the span
 * before's groups alone (`then`). The groups split the span; how many traces
 * there were, and of each agent, is the Overview's read. A part that could
 * not be read is null, but for the span's groups, which must be.
 */
export async function readFleetLinks({ range = FLEET_RANGE } = {}, execute = runQuery) {
  const rows = await run(execute)(linksSpec({ range, source: true }));
  return { links: toPaths(rows) };
}

export async function readFleetBreakdown({ range = FLEET_RANGE, access = {}, before = false } = {}, execute = runQuery) {
  const q = run(execute);
  const read = (span) => Promise.all([
    q(agentGroupsSpec({ range: span, source: true })),
    q(linksSpec({ range: span, source: true })),
  ]);
  const shape = ([groups, links]) => ({ groups: toPaths(groups), links: toPaths(links) });
  if (before) {
    const then = await settle(read(range));
    return { then: then ? shape(then) : null };
  }
  const [now, installed] = await Promise.all([
    read(range),
    access.inventory ? settle(q(installedSpec({}))) : null,
  ]);
  return {
    now: shape(now), then: null,
    installed: installed ? installedBy(installed) : null,
    capped: now[0].length >= GROUP_CAP || now[1].length >= GROUP_CAP,
  };
}

/**
 * The local runtimes seen running in a span, each with the endpoints it ran
 * on and those of them with no local session (lib/fleetBreakdown.js runtimesIn),
 * with `tenant.events.read`; and the runtimes installed. A part that could not
 * be read is null.
 */
export async function readFleetRuntimes({ range = FLEET_RANGE, access = {} } = {}, execute = runQuery) {
  const q = run(execute);
  const [evidence, local, installed] = await Promise.all([
    access.events ? settle(q(evidenceSpec({ range }))) : null,
    access.events ? settle(q(localSpec({ range }))) : null,
    access.inventory ? settle(q(installedSpec({}))) : null,
  ]);
  return { ran: evidence && local ? runtimesIn(evidence, local) : null, installed: installed ? installedBy(installed) : null };
}

/**
 * Fleet's local inference over a span: local sessions by model and endpoint,
 * the runtimes seen running (with `tenant.events.read`), the runtimes
 * installed, and the names and owners of every endpoint among them; with
 * `before`, the span before's sessions and runtimes seen running alone. A part
 * that could not be read is null, but for the span's sessions.
 */
export async function readFleetLocal({ range = FLEET_RANGE, access = {}, before = false } = {}, execute = runQuery) {
  const q = run(execute);
  if (before) {
    const [then, ranThen] = await Promise.all([
      settle(q(localModelsSpec({ range }))),
      access.events ? settle(q(evidenceSpec({ range }))) : null,
    ]);
    return { then, ranThen };
  }
  const [now, ran, installed] = await Promise.all([
    q(localModelsSpec({ range })),
    access.events ? settle(q(evidenceSpec({ range }))) : null,
    access.inventory ? settle(q(runtimesOnSpec())) : null,
  ]);
  const ids = [...new Set([...now, ...(ran || []), ...(installed || [])].map((r) => String(r.endpoint || "")).filter(known))];
  const names = access.inventory && ids.length ? await settle(q(endpointNamesSpec({ ids }))) : null;
  return { now, then: null, ran, ranThen: null, installed, names };
}

/**
 * Some endpoints' sessions over a span by the agent that made them (as paths,
 * lib/fleetBreakdown.js toPaths), or with `before`, their sessions over the span
 * before.
 */
export async function readFleetEndpointAgents({ ids = [], range = FLEET_RANGE, before = false } = {}, execute = runQuery) {
  const shown = [...new Set(ids.filter(known))].slice(0, IN_CAP);
  if (!shown.length) return { now: [], then: [] };
  const q = run(execute);
  if (before) return { then: await settle(q(endpointAgentsSpec({ range, ids: shown, agents: false }))) };
  const now = await q(endpointAgentsSpec({ range, ids: shown }));
  return { now: toPaths(now).map((p, i) => ({ ...p, endpoint: String(now[i].endpoint || "") })), then: null };
}

/**
 * The sessions from any endpoint over a span (`now`) and the span before
 * (`then`, none where it is null), the endpoints the span's came from
 * (`active`), and those whose inventory changed in it (`changed`): each was
 * heard from in the span, whatever its heartbeat says.
 */
export async function readEndpointSessions({ now: span = toApiRange(FLEET_RANGE), then: before = null } = {}, execute = runQuery) {
  const q = run(execute);
  const [now, then, active, changed] = await Promise.all([
    q(endpointSessionsSpec({ range: span })),
    before ? settle(q(endpointSessionsSpec({ range: before }))) : null,
    // ponytail: the first 10,000 endpoints of each; past that, reporting reads low.
    settle(q(activeEndpointsSpec({ range: span }))),
    settle(q(changedEndpointsSpec({ range: span }))),
  ]);
  const ids = (rows) => (rows ? rows.map((r) => String(r.endpoint || "")).filter(known) : null);
  const of = (rows) => (rows ? { sessions: Number(rows[0]?.sessions) || 0, endpoints: Number(rows[0]?.endpoints) || 0 } : null);
  return { window: { now: span, then: before }, now: of(now), then: of(then), active: ids(active), changed: ids(changed) };
}

/**
 * A page of Fleet's endpoints (apiClient getInventoryRows), each with its
 * sessions over the window: 0 where it has none, null where they could not be
 * read, so the page still shows.
 */
export async function withEndpointSessions(page, { range = FLEET_RANGE } = {}, execute = runQuery) {
  const ids = [...new Set(page.rows.map((r) => r.id).filter(known))].slice(0, IN_CAP);
  const rows = ids.length ? await settle(run(execute)({
    root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" }, timeRange: timeRange(range),
    filter: pred(T("ENDPOINT_ID"), "IN", ids),
    output: { dimensions: [dim(T("ENDPOINT_ID"), "endpoint")], metrics: [count(T("TRACE_ID"), "sessions")] },
    limit: ids.length,
  })) : [];
  const by = new Map((rows || []).map((r) => [r.endpoint, Number(r.sessions) || 0]));
  return { ...page, rows: page.rows.map((r) => ({ ...r, sessions: rows ? by.get(r.id) ?? 0 : null })) };
}
